const STORAGE_KEY = 'employeeSetup:drafts'

export const DRAFT_DEBOUNCE_MS = 500
export const MAX_DRAFT_ENTRIES = 50
// Drafts hold personal data, so they expire even without storage pressure.
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export interface SetupDraftKey {
  userUid: string
  employeeUid: string
  step: number
}

export type SetupDraftValues = Record<string, unknown>

interface DraftEntry {
  values: SetupDraftValues
  // savedAt is for TTL/LRU only — never compare it to server timestamps.
  savedAt: number
  baseUpdatedAt: number
}

type DraftMap = Record<string, DraftEntry>

function entryId({ userUid, employeeUid, step }: SetupDraftKey): string {
  return `${userUid}:${employeeUid}:${step}`
}

function isValidEntry(entry: DraftEntry | undefined): entry is DraftEntry {
  return (
    typeof entry?.savedAt === 'number' &&
    Number.isFinite(entry.savedAt) &&
    typeof entry.baseUpdatedAt === 'number' &&
    Number.isFinite(entry.baseUpdatedAt) &&
    typeof entry.values === 'object' &&
    entry.values !== null
  )
}

function readMap(): DraftMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as DraftMap) : {}
  } catch {
    return {}
  }
}

function writeMap(map: DraftMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Best-effort: storage may be unavailable or full.
  }
}

function purgeExpired(map: DraftMap, now: number): boolean {
  let changed = false
  for (const id of Object.keys(map)) {
    const entry = map[id]
    if (!isValidEntry(entry) || now - entry.savedAt > DRAFT_TTL_MS) {
      delete map[id]
      changed = true
    }
  }
  return changed
}

function prune(map: DraftMap, now: number): void {
  purgeExpired(map, now)
  const ids = Object.keys(map)
  if (ids.length <= MAX_DRAFT_ENTRIES) {
    return
  }
  ids.sort((a, b) => map[a].savedAt - map[b].savedAt)
  for (const id of ids.slice(0, ids.length - MAX_DRAFT_ENTRIES)) {
    delete map[id]
  }
}

// Loading removes expired entries and drafts based on another employee revision.
export function loadDraft({
  userUid,
  employeeUid,
  step,
  currentUpdatedAt,
}: SetupDraftKey & { currentUpdatedAt: number }): SetupDraftValues | null {
  const map = readMap()
  const now = Date.now()
  let changed = purgeExpired(map, now)

  const id = entryId({ userUid, employeeUid, step })
  const entry = map[id]
  let values: SetupDraftValues | null = null
  if (entry) {
    if (entry.baseUpdatedAt === currentUpdatedAt) {
      values = entry.values
    } else {
      delete map[id]
      changed = true
    }
  }

  if (changed) {
    writeMap(map)
  }
  return values
}

export function saveDraft(
  key: SetupDraftKey,
  values: SetupDraftValues,
  baseUpdatedAt: number
): void {
  const map = readMap()
  const now = Date.now()
  map[entryId(key)] = { values, savedAt: now, baseUpdatedAt }
  prune(map, now)
  writeMap(map)
}

export function clearDraft(key: SetupDraftKey): void {
  const map = readMap()
  const id = entryId(key)
  if (!(id in map)) {
    return
  }
  delete map[id]
  writeMap(map)
}

export function clearAllForUser(userUid: string): void {
  const map = readMap()
  const prefix = `${userUid}:`
  let changed = false
  for (const id of Object.keys(map)) {
    if (id.startsWith(prefix)) {
      delete map[id]
      changed = true
    }
  }
  if (changed) {
    writeMap(map)
  }
}
